import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Avatar,
  AvatarGroup,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  Snackbar,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import {
  LocationOn as LocationOnIcon,
  Description as DescriptionIcon,
  Close as CloseIcon,
  Flight as FlightIcon,
  Add as AddIcon,
  Event as EventIcon,
  FilterAlt as FilterIcon,
  Today as TodayIcon,
  ViewDay as DayViewIcon,
  ViewWeek as WeekViewIcon,
  ViewModule as MonthViewIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  CalendarToday as CalendarIcon,
  CloudUpload as CloudUploadIcon,
  Google as GoogleIcon,
  CloudSync as CloudSyncIcon
} from '@mui/icons-material';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { Calendar, dateFnsLocalizer, View } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { format, parse, startOfWeek, getDay, addMonths, subMonths, isSameDay } from 'date-fns';
import enUS from 'date-fns/locale/en-US';
import { collection, query, where, orderBy, getDocs, Timestamp, addDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db } from '../../../firebase/config';
import { useUser } from '../../../contexts/UserContext';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { getMultiYearHolidays, Holiday } from '../../../utils/holidayUtils';
import './calendarStyles.css'; // Add custom CSS styles
import GoogleCalendarSyncDialog from '../../../components/GoogleCalendarSync/GoogleCalendarSyncDialog';

// Initialize Firebase Storage
const storage = getStorage();

// Calendar localization setup
const locales = {
  'en-US': enUS,
};

// Custom parse function to match react-big-calendar's expected signature
const customParseDate = (dateString: string, formatString: string) => {
  return parse(dateString, formatString, new Date());
};

const localizer = dateFnsLocalizer({
  format,
  parse: customParseDate,
  startOfWeek,
  getDay,
  locales,
});

// Types
interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  allDay?: boolean;
  type: 'trip' | 'event' | 'holiday';
  resource?: any;
  coverImageUrl?: string;
}

interface FilterOptions {
  showTrips: boolean;
  showEvents: boolean;
  showHolidays: boolean;
}

// Define ToolbarProps interface
interface ToolbarProps {
  label: string;
  view: string;
  views: string[];
  onNavigate: (action: 'PREV' | 'NEXT' | 'TODAY') => void;
  onView: (view: string) => void;
}

// Define TripData interface
interface TripData {
  title: string;
  description: string;
  startDate: Date;
  endDate: Date;
  destination: string;
  organizationId: string;
  createdBy: string;
  coverImageUrl?: string;
}

// Custom Toolbar Component
const CustomToolbar: React.FC<ToolbarProps> = ({ label, view, views, onNavigate, onView }) => {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
      <Box>
        <Button 
          variant="outlined" 
          size="small" 
          onClick={() => onNavigate('TODAY')}
          startIcon={<TodayIcon />}
          sx={{ mr: 1 }}
        >
          Today
        </Button>
        <IconButton onClick={() => onNavigate('PREV')}>
          <ChevronLeftIcon />
        </IconButton>
        <IconButton onClick={() => onNavigate('NEXT')}>
          <ChevronRightIcon />
        </IconButton>
        <Typography variant="h6" component="span" sx={{ mx: 2 }}>
          {label}
        </Typography>
      </Box>
      <Box>
        <Tooltip title="Day View">
          <IconButton 
            color={view === 'day' ? 'primary' : 'default'} 
            onClick={() => onView('day')}
          >
            <DayViewIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Week View">
          <IconButton 
            color={view === 'week' ? 'primary' : 'default'} 
            onClick={() => onView('week')}
          >
            <WeekViewIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Month View">
          <IconButton 
            color={view === 'month' ? 'primary' : 'default'} 
            onClick={() => onView('month')}
          >
            <MonthViewIcon />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

// Custom Year View Component
const YearView: React.FC<any> = ({ events, date, localizer, onSelectEvent }) => {
  const currentYear = date.getFullYear();
  const months = Array.from({ length: 12 }, (_, i) => new Date(currentYear, i, 1));
  
  // Function to get events for a specific month
  const getEventsForMonth = (month: Date) => {
    const startOfMonth = new Date(month.getFullYear(), month.getMonth(), 1);
    const endOfMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0);
    
    return events.filter((event: CalendarEvent) => {
      const eventStart = new Date(event.start);
      const eventEnd = new Date(event.end);
      
      return (
        (eventStart >= startOfMonth && eventStart <= endOfMonth) || 
        (eventEnd >= startOfMonth && eventEnd <= endOfMonth) ||
        (eventStart <= startOfMonth && eventEnd >= endOfMonth)
      );
    });
  };
  
  return (
    <Box sx={{ overflowY: 'auto', height: '100%' }}>
      <Grid container spacing={3}>
        {months.map((month, index) => (
          <Grid item xs={12} key={index}>
            <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                {localizer.format(month, 'MMMM yyyy')}
              </Typography>
              <Grid container spacing={1}>
                {getEventsForMonth(month).map((event: CalendarEvent) => (
                  <Grid item xs={12} key={event.id}>
                    <Box 
                      sx={{ 
                        p: 1.5, 
                        borderRadius: 1,
                        cursor: 'pointer',
                        backgroundColor: event.type === 'trip' ? '#e3f2fd' : event.type === 'holiday' ? '#9e9e9e' : '#e8eaf6',
                        color: event.type === 'trip' ? '#1565c0' : event.type === 'holiday' ? '#fff' : '#3f51b5',
                        borderLeft: event.type === 'trip' ? '4px solid #1565c0' : event.type === 'holiday' ? '4px solid #9e9e9e' : '4px solid #3f51b5',
                        border: '1px solid #e0e0e0',
                        mb: 0.5,
                        display: 'flex',
                        alignItems: 'center'
                      }}
                      onClick={() => onSelectEvent(event)}
                    >
                      {event.type === 'trip' ? <FlightIcon sx={{ mr: 1, fontSize: 18 }} /> : event.type === 'holiday' ? <CalendarIcon sx={{ mr: 1, fontSize: 18 }} /> : <EventIcon sx={{ mr: 1, fontSize: 18 }} />}
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                          {event.title}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block' }}>
                          {localizer.format(new Date(event.start), 'MMM dd')} - {localizer.format(new Date(event.end), 'MMM dd')}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
                {getEventsForMonth(month).length === 0 && (
                  <Grid item xs={12}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', py: 2, textAlign: 'center' }}>
                      No events for this month
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

// Holiday Component for Custom Rendering
const HolidayEventComponent = ({ event }: { event: CalendarEvent }) => {
  // Add null check to handle undefined events
  if (!event || !event.title) {
    return null;
  }
  
  // Truncate long titles for holidays
  const displayTitle = event.title.length > 22 ? `${event.title.substring(0, 19)}...` : event.title;
  
  return (
    <Box sx={{ 
      height: '16px',
      display: 'flex', 
      alignItems: 'center',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      bgcolor: event.resource?.color || '#9e9e9e',
      color: '#fff',
      borderRadius: 0.5,
      px: 1,
      width: '100%',
      fontSize: '0.75rem',
      fontStyle: 'italic',
      opacity: 0.7,
      '&:hover': {
        opacity: 0.9
      }
    }}>
      {displayTitle}
    </Box>
  );
};

// Event Component for Custom Rendering
const EventComponent = ({ event }: { event: CalendarEvent }) => {
  // Add null check to handle undefined events
  if (!event || !event.title) {
    return <Box sx={{ height: '100%', bgcolor: '#f5f5f5', px: 1 }}>Unknown event</Box>;
  }
  
  // Truncate long titles to prevent layout issues
  const displayTitle = event.title.length > 25 ? `${event.title.substring(0, 22)}...` : event.title;
  
  return (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      alignItems: 'center',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      bgcolor: event.type === 'trip' ? 'secondary.light' : event.type === 'holiday' ? '#9e9e9e' : 'primary.light',
      color: event.type === 'trip' ? 'secondary.contrastText' : event.type === 'holiday' ? '#fff' : 'primary.contrastText',
      borderRadius: 1,
      px: 1,
      width: '100%',
      fontSize: '0.85rem', // Slightly smaller font size to fit more content
      opacity: 0.9,
      '&:hover': {
        opacity: 1
      }
    }}>
      {displayTitle}
    </Box>
  );
};

const OrganizationCalendar: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { activeOrganization, isPersonalAccount, switchToOrganization } = useOrganization();
  const { currentUser } = useUser();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [calendarView, setCalendarView] = useState<View | string>('month');
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    showTrips: true,
    showEvents: true,
    showHolidays: true
  });
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);

  // Dialog states for creating events and trips
  const [addEventDialogOpen, setAddEventDialogOpen] = useState(false);
  const [addTripDialogOpen, setAddTripDialogOpen] = useState(false);
  const [eventLoading, setEventLoading] = useState(false);
  const [tripLoading, setTripLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');

  // Form data for new event and trip
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    startDate: new Date(),
    endDate: new Date(),
    location: '',
    coverImageUrl: '',
    type: 'social' as 'social' | 'business' | 'family' | 'cultural' | 'sports' | 'other'
  });

  const [newTrip, setNewTrip] = useState({
    title: '',
    description: '',
    startDate: new Date(),
    endDate: new Date(),
    destination: '',
    coverImageUrl: ''
  });

  // State for handling file uploads
  const [eventImageFile, setEventImageFile] = useState<File | null>(null);
  const [eventImagePreview, setEventImagePreview] = useState<string | null>(null);
  const [tripImageFile, setTripImageFile] = useState<File | null>(null);
  const [tripImagePreview, setTripImagePreview] = useState<string | null>(null);

  // Google Calendar sync state
  const [googleCalendarSyncOpen, setGoogleCalendarSyncOpen] = useState(false);
  const [selectedEventForSync, setSelectedEventForSync] = useState<string | undefined>(undefined);

  // Function to fetch organization's trips and events
  const fetchOrganizationCalendarData = useCallback(async (organizationId: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const events: CalendarEvent[] = [];
      
      // Fetch organization's trips
      if (filterOptions.showTrips) {
        const tripsQuery = query(
          collection(db, 'trips'),
          where('organizationId', '==', organizationId),
          orderBy('startDate', 'asc')
        );
        
        const tripsSnapshot = await getDocs(tripsQuery);
        
        tripsSnapshot.forEach(doc => {
          const data = doc.data();
          events.push({
            id: doc.id,
            title: data.title,
            start: data.startDate instanceof Timestamp ? data.startDate.toDate() : new Date(data.startDate),
            end: data.endDate instanceof Timestamp ? data.endDate.toDate() : new Date(data.endDate),
            allDay: true,
            type: 'trip',
            resource: {
              ...data,
              organizationId
            }
          });
        });
      }
      
      // Fetch organization's events
      if (filterOptions.showEvents) {
        const eventsQuery = query(
          collection(db, 'events'),
          where('organizationId', '==', organizationId),
          orderBy('startDate', 'asc')
        );
        
        const eventsSnapshot = await getDocs(eventsQuery);
        
        eventsSnapshot.forEach(doc => {
          const data = doc.data();
          events.push({
            id: doc.id,
            title: data.title,
            start: data.startDate instanceof Timestamp ? data.startDate.toDate() : new Date(data.startDate),
            end: data.endDate instanceof Timestamp 
              ? data.endDate.toDate() 
              : (data.startDate instanceof Timestamp 
                ? new Date(data.startDate.toDate().getTime() + 2 * 60 * 60 * 1000) 
                : new Date(new Date(data.startDate).getTime() + 2 * 60 * 60 * 1000)),
            allDay: false,
            type: 'event',
            resource: {
              ...data,
              organizationId
            }
          });
        });
      }
      
      // Add national holidays
      if (filterOptions.showHolidays) {
        // Get current year and next year to ensure we have holidays for the visible calendar range
        const currentYear = new Date().getFullYear();
        const holidays = getMultiYearHolidays(currentYear - 1, currentYear + 1);
        
        // Convert Holiday type to CalendarEvent type and add to events array
        holidays.forEach(holiday => {
          events.push({
            id: holiday.id,
            title: holiday.title,
            start: holiday.start,
            end: holiday.end,
            allDay: holiday.allDay,
            type: 'holiday',
            resource: {
              category: holiday.category,
              color: holiday.color
            }
          });
        });
      }
      
      setCalendarEvents(events);
    } catch (err) {
      console.error('Error fetching organization calendar data:', err);
      setError('Failed to load calendar data. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [filterOptions]);

  useEffect(() => {
    // If we have an organization ID but we're in personal account mode,
    // switch to the organization
    if (id && isPersonalAccount) {
      switchToOrganization(id);
    }

    // Initialize calendar
    if (id && activeOrganization) {
      fetchOrganizationCalendarData(id);
    }
  }, [id, isPersonalAccount, switchToOrganization, activeOrganization, fetchOrganizationCalendarData]);

  const handleEventSelect = (event: CalendarEvent) => {
    setSelectedEvent(event);
    setDetailsOpen(true);
  };

  const handleFilterClick = () => {
    setFilterDialogOpen(true);
  };

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterOptions({
      ...filterOptions,
      [event.target.name]: event.target.checked
    });
  };

  const handleFilterApply = () => {
    setFilterDialogOpen(false);
    
    // Refetch calendar data with new filters
    if (id) {
      fetchOrganizationCalendarData(id);
    }
  };

  // Event form handlers
  const handleEventChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewEvent({
      ...newEvent,
      [event.target.name]: event.target.value
    });
  };

  const handleEventDateChange = (date: Date, field: 'startDate' | 'endDate') => {
    setNewEvent({
      ...newEvent,
      [field]: date
    });
  };

  const resetEventForm = () => {
    setNewEvent({
      title: '',
      description: '',
      startDate: new Date(),
      endDate: new Date(),
      location: '',
      coverImageUrl: '',
      type: 'social'
    });
  };

  const handleAddEvent = async () => {
    if (!newEvent.title) {
      setSnackbarMessage('Please provide a title for the event');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    setEventLoading(true);
    try {
       const eventData = {
          title: newEvent.title,
          description: newEvent.description,
          startDate: newEvent.startDate,
          endDate: newEvent.endDate,
          location: newEvent.location,
          type: newEvent.type,
          organizationId: id || '',
          createdBy: currentUser?.uid || '',
          coverImageUrl: '', // Initialize with empty string
       };

       // Upload event image if selected
       if (eventImageFile) {
          const imageRef = ref(storage, `images/${eventImageFile.name}`);
          await uploadBytes(imageRef, eventImageFile);
          const url = await getDownloadURL(imageRef);
          eventData.coverImageUrl = url;
       }

       // Use backend API to create event with proper members array
       const { createApiUrl } = await import('../../../utils/apiConfig');
       const { getAuthHeaders } = await import('../../../utils/auth');
       const apiUrl = createApiUrl('/api/events');
       const headers = await getAuthHeaders();

       const response = await fetch(apiUrl, {
          method: 'POST',
          headers,
          body: JSON.stringify({
             title: newEvent.title,
             description: newEvent.description,
             startDate: newEvent.startDate.toISOString(),
             endDate: newEvent.endDate.toISOString(),
             location: newEvent.location,
             eventType: newEvent.type,
             isPublic: false,
             coverImageUrl: eventData.coverImageUrl,
             organizationId: id || '',
             userId: currentUser?.uid || '',
          }),
       });

       const result = await response.json();

       if (!response.ok) {
          throw new Error(result.error || 'Failed to create event');
       }

       const docRef = { id: result.eventId };

       // Immediately add the new event to the calendar
       const newCalendarEvent: CalendarEvent = {
          id: docRef.id,
          title: newEvent.title,
          start: newEvent.startDate,
          end: newEvent.endDate,
          type: 'event',
          resource: {
             ...eventData,
             id: docRef.id,
             createdAt: new Date(),
             updatedAt: new Date(),
          },
       };

       // Update local state with the new event
       setCalendarEvents((prev) => [...prev, newCalendarEvent]);

       // Show success message
       setSnackbarMessage('Event created successfully');
       setSnackbarSeverity('success');
       setSnackbarOpen(true);

       // Close dialog and reset form
       setAddEventDialogOpen(false);
       resetEventForm();
       setEventImageFile(null);
       setEventImagePreview(null);
    } catch (error) {
      console.error('Error creating event:', error);
      setSnackbarMessage('Failed to create event');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setEventLoading(false);
    }
  };

  // Trip form handlers
  const handleTripChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewTrip({
      ...newTrip,
      [event.target.name]: event.target.value
    });
  };

  const handleTripDateChange = (date: Date, field: 'startDate' | 'endDate') => {
    setNewTrip({
      ...newTrip,
      [field]: date
    });
  };

  const resetTripForm = () => {
    setNewTrip({
      title: '',
      description: '',
      startDate: new Date(),
      endDate: new Date(),
      destination: '',
      coverImageUrl: ''
    });
  };

  const handleAddTrip = async () => {
    if (!newTrip.title || !newTrip.destination) {
      setSnackbarMessage('Please provide a title and destination for the trip');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    setTripLoading(true);
    try {
      const tripData: TripData = {
        title: newTrip.title,
        description: newTrip.description,
        startDate: newTrip.startDate,
        endDate: newTrip.endDate,
        destination: newTrip.destination,
        organizationId: id || '',
        createdBy: currentUser?.uid || ''
      };

      // Upload trip image if selected
      if (tripImageFile) {
        const imageRef = ref(storage, `images/${tripImageFile.name}`);
        await uploadBytes(imageRef, tripImageFile);
        const url = await getDownloadURL(imageRef);
        tripData.coverImageUrl = url;
      }

      // Add trip to Firestore
      const tripRef = collection(db, 'trips');
      const docRef = await addDoc(tripRef, {
        ...tripData,
        startDate: Timestamp.fromDate(newTrip.startDate),
        endDate: Timestamp.fromDate(newTrip.endDate),
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        accommodations: [],
        locations: [],
        photos: []
      });

      // Immediately add the new trip to the calendar
      const newCalendarEvent: CalendarEvent = {
        id: docRef.id,
        title: newTrip.title,
        start: newTrip.startDate,
        end: newTrip.endDate,
        type: 'trip',
        resource: {
          ...tripData,
          id: docRef.id,
          createdAt: new Date(),
          updatedAt: new Date(),
          accommodations: [],
          locations: [],
          photos: []
        }
      };
      
      // Update local state with the new trip
      setCalendarEvents(prev => [...prev, newCalendarEvent]);

      // Show success message
      setSnackbarMessage('Trip created successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      // Close dialog and reset form
      setAddTripDialogOpen(false);
      resetTripForm();
      setTripImageFile(null);
      setTripImagePreview(null);
    } catch (error) {
      console.error('Error creating trip:', error);
      setSnackbarMessage('Failed to create trip');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setTripLoading(false);
    }
  };

  const handleEventImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setEventImageFile(event.target.files[0]);
      setEventImagePreview(URL.createObjectURL(event.target.files[0]));
    }
  };

  const handleTripImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setTripImageFile(event.target.files[0]);
      setTripImagePreview(URL.createObjectURL(event.target.files[0]));
    }
  };

  const handleOpenGoogleCalendarSync = (eventId?: string) => {
    setSelectedEventForSync(eventId);
    setGoogleCalendarSyncOpen(true);
  };

  const handleCloseGoogleCalendarSync = () => {
    setGoogleCalendarSyncOpen(false);
    setSelectedEventForSync(undefined);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4, px: { xs: 1, sm: 2, md: 3 } }}>
      <Paper sx={{ p: { xs: 1, sm: 2, md: 2 }, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {activeOrganization?.name || 'Organization'} Calendar
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              sx={{ mr: 2 }}
              onClick={handleFilterClick}
            >
              Filter
            </Button>
            
            {/* Create Event Button */}
            <Button
              variant="contained"
              color="primary"
              startIcon={<EventIcon />}
              sx={{ mr: 2 }}
              onClick={() => setAddEventDialogOpen(true)}
            >
              Create Event
            </Button>
            
            {/* Create Trip Button */}
            <Button
              variant="contained"
              color="secondary"
              startIcon={<FlightIcon />}
              onClick={() => setAddTripDialogOpen(true)}
            >
              Create Trip
            </Button>
            
            {/* Google Calendar Sync Button */}
            <Button
              variant="outlined"
              startIcon={<GoogleIcon />}
              onClick={() => handleOpenGoogleCalendarSync()}
              sx={{ mr: 1 }}
            >
              Sync to Google Calendar
            </Button>
          </Box>
        </Box>
        <Typography variant="body1" color="text.secondary">
          View and manage organization events
        </Typography>
      </Paper>
      
      {/* Calendar */}
      <Paper 
        sx={{ 
          p: { xs: 1, sm: 2, md: 2 }, 
          minHeight: '700px', 
          borderRadius: 2
        }}
      >
        <ToggleButtonGroup
          value={calendarView}
          exclusive
          onChange={(event, newView) => setCalendarView(newView)}
          aria-label="text formatting"
        >
          <ToggleButton value="month" aria-label="month">
            <MonthViewIcon />
          </ToggleButton>
          <ToggleButton value="year" aria-label="year">
            <CalendarIcon />
          </ToggleButton>
        </ToggleButtonGroup>
        
        {calendarView === 'year' ? (
          <YearView 
            events={calendarEvents} 
            date={currentDate} 
            localizer={localizer} 
            onSelectEvent={handleEventSelect} 
          />
        ) : (
          <Calendar
            localizer={localizer}
            events={calendarEvents}
            startAccessor="start"
            endAccessor="end"
            style={{ height: 700 }}
            defaultView={'month'}
            view={calendarView as View}
            onView={(view) => setCalendarView(view)}
            date={currentDate}
            onNavigate={(date) => setCurrentDate(date)}
            components={{
              toolbar: CustomToolbar as any,
              event: (event) => event.type === 'holiday' ? <HolidayEventComponent event={event} /> : <EventComponent event={event} />
            }}
            onSelectEvent={(event) => handleEventSelect(event as CalendarEvent)}
            eventPropGetter={(event) => {
              // Add null check to handle undefined events
              if (!event) {
                return {};
              }
              
              const calEvent = event as unknown as CalendarEvent;
              
              if (calEvent.type === 'holiday') {
                // Style for holidays based on category
                const category = calEvent.resource?.category || 'cultural';
                let backgroundColor = calEvent.resource?.color || '#9e9e9e';
                
                return {
                  className: 'holiday-event',
                  style: {
                    backgroundColor,
                    color: '#fff',
                    borderLeft: `4px solid ${backgroundColor}`,
                    opacity: 0.8,
                    fontStyle: 'italic',
                    height: '16px', // Fixed smaller height for holidays
                    padding: '0 2px',
                    margin: '0 0 1px 0',
                    fontSize: '0.8rem',
                    position: 'relative',
                    zIndex: 1 // Lower z-index to prevent overlapping
                  }
                };
              }
              
              return {
                className: calEvent.type === 'trip' ? 'trip-event' : 'regular-event',
                style: {
                  backgroundColor: calEvent.type === 'trip' ? '#e3f2fd' : '#e8eaf6',
                  color: calEvent.type === 'trip' ? '#1565c0' : '#3f51b5',
                  borderLeft: calEvent.type === 'trip' ? '4px solid #1565c0' : '4px solid #3f51b5',
                  border: '1px solid #e0e0e0',
                  position: 'relative',
                  zIndex: calEvent.type === 'trip' ? 2 : 3 // Trips have medium z-index, regular events highest
                }
              };
            }}
            dayLayoutAlgorithm="no-overlap"
            dayPropGetter={(date) => ({
              style: {
                minHeight: 110, // Ensure days have enough height for multiple events
              },
            })}
            slotGroupPropGetter={() => ({
              style: {
                minHeight: 60,
              },
            })}
            popup
            popupOffset={30}
            // Sort events to ensure holidays appear at the top
            sortEvents={(eventA: CalendarEvent, eventB: CalendarEvent) => {
              // Always put holidays first
              if (eventA.type === 'holiday' && eventB.type !== 'holiday') return -1;
              if (eventA.type !== 'holiday' && eventB.type === 'holiday') return 1;
              
              // Then put trips second
              if (eventA.type === 'trip' && eventB.type !== 'trip' && eventB.type !== 'holiday') return -1;
              if (eventA.type !== 'trip' && eventA.type !== 'holiday' && eventB.type === 'trip') return 1;
              
              // Default sorting by start time
              return eventA.start.getTime() - eventB.start.getTime();
            }}
          />
        )}
      </Paper>

      {/* Filter Dialog */}
      <Dialog open={filterDialogOpen} onClose={() => setFilterDialogOpen(false)}>
        <DialogTitle>Filter Calendar</DialogTitle>
        <DialogContent>
          <FormControl component="fieldset">
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox 
                    checked={filterOptions.showEvents} 
                    onChange={handleFilterChange} 
                    name="showEvents" 
                  />
                }
                label="Show Events"
              />
              <FormControlLabel
                control={
                  <Checkbox 
                    checked={filterOptions.showTrips} 
                    onChange={handleFilterChange} 
                    name="showTrips" 
                  />
                }
                label="Show Trips"
              />
              <FormControlLabel
                control={
                  <Checkbox 
                    checked={filterOptions.showHolidays} 
                    onChange={handleFilterChange} 
                    name="showHolidays" 
                  />
                }
                label="Show Holidays"
              />
            </FormGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFilterDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleFilterApply} variant="contained" color="primary">Apply</Button>
        </DialogActions>
      </Dialog>

      {/* Event Details Dialog */}
      {selectedEvent && (
        <Dialog 
          open={detailsOpen} 
          onClose={() => setDetailsOpen(false)} 
          maxWidth="md" 
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              overflow: 'hidden',
            }
          }}
        >
          {/* Cover Photo Section */}
          <Box sx={{ position: 'relative' }}>
            <Box 
              sx={{ 
                height: 200, 
                bgcolor: selectedEvent.type === 'trip' ? 'secondary.light' : selectedEvent.type === 'holiday' ? '#9e9e9e' : 'primary.light',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden'
              }}
            >
              {(selectedEvent.coverImageUrl || selectedEvent.resource?.coverPhoto) ? (
                <img 
                  src={selectedEvent.coverImageUrl || selectedEvent.resource?.coverPhoto} 
                  alt={selectedEvent.title}
                  style={{ 
                    width: '100%', 
                    height: '100%', 
                    objectFit: 'cover'
                  }} 
                />
              ) : (
                <Box sx={{ 
                  position: 'absolute', 
                  width: '100%', 
                  height: '100%',
                  background: selectedEvent.type === 'trip' 
                    ? 'linear-gradient(135deg, #bbdefb 0%, #90caf9 100%)' 
                    : selectedEvent.type === 'holiday' 
                      ? 'linear-gradient(135deg, #d1c4e9 0%, #b39ddb 100%)' 
                      : 'linear-gradient(135deg, #d1c4e9 0%, #b39ddb 100%)'
                }} />
              )}
              
              {/* Overlay with gradient */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(rgba(0,0,0,0), rgba(0,0,0,0.4))',
                  zIndex: 1
                }}
              />
              
              {/* Event Type Badge */}
              <Chip 
                label={selectedEvent.type === 'trip' ? 'Trip' : selectedEvent.type === 'holiday' ? 'Holiday' : 'Event'} 
                color={selectedEvent.type === 'trip' ? 'secondary' : selectedEvent.type === 'holiday' ? 'default' : 'primary'}
                size="small"
                sx={{ 
                  position: 'absolute', 
                  top: 16, 
                  right: 16,
                  zIndex: 2,
                  fontWeight: 600
                }}
              />
            </Box>
            
            {/* Event Title */}
            <Box 
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                width: '100%',
                p: 3,
                zIndex: 2
              }}
            >
              <Typography 
                variant="h4" 
                component="h2" 
                sx={{ 
                  color: 'white', 
                  textShadow: '0 2px 4px rgba(0,0,0,0.5)',
                  fontWeight: 600
                }}
              >
                {selectedEvent.title}
              </Typography>
            </Box>
          </Box>
          
          <DialogContent sx={{ pt: 3, px: 3, pb: 1 }}>
            {/* Details Section with Icons */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CalendarIcon color="action" />
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {format(selectedEvent.start, 'PPP')}
                    {!isSameDay(selectedEvent.start, selectedEvent.end) && 
                      ` - ${format(selectedEvent.end, 'PPP')}`
                    }
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {format(selectedEvent.start, 'p')} 
                    {!selectedEvent.allDay && ` - ${format(selectedEvent.end, 'p')}`}
                    {selectedEvent.allDay && ' (All day)'}
                  </Typography>
                </Box>
              </Box>
              
              {selectedEvent.resource?.location && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <LocationOnIcon color="action" />
                  <Typography variant="body1">
                    {typeof selectedEvent.resource.location === 'string' 
                      ? selectedEvent.resource.location 
                      : selectedEvent.resource.location.name
                    }
                  </Typography>
                </Box>
              )}
              
              {selectedEvent.resource?.description && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mt: 1 }}>
                  <DescriptionIcon color="action" sx={{ mt: 0.5 }} />
                  <Typography variant="body1">
                    {selectedEvent.resource.description}
                  </Typography>
                </Box>
              )}
            </Box>
            
            {/* Additional Event Details - can be expanded as needed */}
            {selectedEvent.resource?.attendees && selectedEvent.resource.attendees.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 1 }}>
                  Attendees
                </Typography>
                <AvatarGroup max={5} sx={{ justifyContent: 'flex-start' }}>
                  {selectedEvent.resource.attendees.map((attendee: any, index: number) => (
                    <Avatar 
                      key={index} 
                      alt={attendee.name || 'User'} 
                      src={attendee.photoURL || undefined} 
                    />
                  ))}
                </AvatarGroup>
              </Box>
            )}
          </DialogContent>
          
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button 
              onClick={() => setDetailsOpen(false)}
              variant="outlined"
              startIcon={<CloseIcon />}
            >
              Close
            </Button>
            <Button 
              startIcon={<GoogleIcon />}
              onClick={() => {
                if (selectedEvent) {
                  handleOpenGoogleCalendarSync(selectedEvent.id);
                }
                setDetailsOpen(false);
              }}
              color="primary"
            >
              Add to Google Calendar
            </Button>
            <Button 
              variant="contained" 
              color={selectedEvent.type === 'trip' ? 'secondary' : selectedEvent.type === 'holiday' ? 'inherit' : 'primary'}
              startIcon={selectedEvent.type === 'trip' ? <FlightIcon /> : selectedEvent.type === 'holiday' ? <CalendarIcon /> : <EventIcon />}
              href={selectedEvent.type === 'trip' 
                ? `/organizations/${id}/trips/${selectedEvent.id}` 
                : selectedEvent.type === 'holiday' 
                  ? `/organizations/${id}/holidays/${selectedEvent.id}` 
                  : `/organizations/${id}/events/${selectedEvent.id}`
              }
            >
              View Details
            </Button>
          </DialogActions>
        </Dialog>
      )}
      
      {/* Add Event Dialog */}
      <Dialog
        open={addEventDialogOpen}
        onClose={() => setAddEventDialogOpen(false)}
        sx={{
          '& .MuiDialog-paper': {
            width: '100%',
            maxWidth: '600px',
            borderRadius: 2,
            p: 2,
          },
        }}
      >
        <DialogTitle>
          Create Event
          <IconButton
            aria-label="close"
            onClick={() => setAddEventDialogOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Event Title"
                value={newEvent.title}
                onChange={handleEventChange}
                name="title"
                size="small"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Event Description"
                value={newEvent.description}
                onChange={handleEventChange}
                name="description"
                multiline
                rows={4}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                  label="Start Date & Time"
                  value={newEvent.startDate}
                  onChange={(date) => date && handleEventDateChange(date, 'startDate')}
                  renderInput={params => (
                    <TextField {...params} size='small' fullWidth />
                  )}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                  label="End Date & Time"
                  value={newEvent.endDate}
                  onChange={(date) => date && handleEventDateChange(date, 'endDate')}
                  renderInput={params => (
                    <TextField {...params} size='small' fullWidth />
                  )}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Location"
                value={newEvent.location}
                onChange={handleEventChange}
                name="location"
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Event Type</InputLabel>
                <Select
                  value={newEvent.type}
                  label="Event Type"
                  name="type"
                  onChange={handleEventChange as any}
                >
                  <MenuItem value="social">Social Gathering</MenuItem>
                  <MenuItem value="business">Business Event</MenuItem>
                  <MenuItem value="family">Family Event</MenuItem>
                  <MenuItem value="cultural">Cultural Event</MenuItem>
                  <MenuItem value="sports">Sports Event</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Cover Image URL"
                value={newEvent.coverImageUrl}
                onChange={handleEventChange}
                name="coverImageUrl"
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                component="label"
                startIcon={<CloudUploadIcon />}
              >
                Upload Cover Image
                <input
                  type="file"
                  hidden
                  onChange={handleEventImageChange}
                />
              </Button>
              {eventImagePreview && (
                <Box sx={{ mt: 2 }}>
                  <img src={eventImagePreview} alt="Uploaded Image" style={{ width: '100%' }} />
                </Box>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ pb: 2, px: 3 }}>
          <Button onClick={() => setAddEventDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleAddEvent}
            variant="contained"
            color="primary"
            disabled={eventLoading}
          >
            {eventLoading ? 'Creating...' : 'Create Event'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Add Trip Dialog */}
      <Dialog
        open={addTripDialogOpen}
        onClose={() => setAddTripDialogOpen(false)}
        sx={{
          '& .MuiDialog-paper': {
            width: '100%',
            maxWidth: '600px',
            borderRadius: 2,
            p: 2,
          },
        }}
      >
        <DialogTitle>
          Create Trip
          <IconButton
            aria-label="close"
            onClick={() => setAddTripDialogOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Trip Title"
                value={newTrip.title}
                onChange={handleTripChange}
                name="title"
                size="small"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Trip Description"
                value={newTrip.description}
                onChange={handleTripChange}
                name="description"
                multiline
                rows={4}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                  label="Start Date"
                  value={newTrip.startDate}
                  onChange={(date) => date && handleTripDateChange(date, 'startDate')}
                  renderInput={params => (
                    <TextField {...params} size='small' fullWidth />
                  )}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                  label="End Date"
                  value={newTrip.endDate}
                  onChange={(date) => date && handleTripDateChange(date, 'endDate')}
                  renderInput={params => (
                    <TextField {...params} size='small' fullWidth />
                  )}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Destination"
                value={newTrip.destination}
                onChange={handleTripChange}
                name="destination"
                size="small"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Cover Image URL"
                value={newTrip.coverImageUrl}
                onChange={handleTripChange}
                name="coverImageUrl"
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                component="label"
                startIcon={<CloudUploadIcon />}
              >
                Upload Cover Image
                <input
                  type="file"
                  hidden
                  onChange={handleTripImageChange}
                />
              </Button>
              {tripImagePreview && (
                <Box sx={{ mt: 2 }}>
                  <img src={tripImagePreview} alt="Uploaded Image" style={{ width: '100%' }} />
                </Box>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ pb: 2, px: 3 }}>
          <Button onClick={() => setAddTripDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleAddTrip}
            variant="contained"
            color="secondary"
            disabled={tripLoading}
          >
            {tripLoading ? 'Creating...' : 'Create Trip'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Google Calendar Sync Dialog */}
      <GoogleCalendarSyncDialog
        open={googleCalendarSyncOpen}
        onClose={handleCloseGoogleCalendarSync}
        events={calendarEvents}
        selectedEventId={selectedEventForSync}
        organizationName={activeOrganization?.name || 'Organization'}
      />
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MuiAlert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </Container>
  );
};

export default OrganizationCalendar;